import { z } from "zod";

import { RelationshipToSubscriber } from "@/packages/shared/src/models/coverage";
import payers from "@/src/data/payers.json";
import { validateObject, ObjectErrors } from "@/src/lib/utils/object-utils";
import { simpleDateSchema, phoneNumberSchema } from "@/src/models/common-schema";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { Patient } from "@/src/models/patient";
import { Person, PersonGender, personNamesMatch } from "@/src/models/person";

export enum CoverageType {
  INSURANCE = "Health Insurance",
  SELF_PAY = "Self Pay",
}

export enum LineOfBusiness {
  MEDICARE = "Medicare",
  MEDICAID = "Medicaid",
  COMMERCIAL = "Commercial",
  EXCHANGE = "Exchange",
  UNKNOWN = "Unknown",
}

// Single schema for all coverage types
export const coverageSchema = z
  .object({
    coverageType: z.nativeEnum(CoverageType).describe("The type of coverage, i.e. Insurance or Self Pay"),
    order: z
      .number()
      .nullable()
      .describe("The order of the coverage in the patient's coverage list, i.e. primary, secondary, etc."),
    effectiveDate: simpleDateSchema.nullable().describe(`
    Extract the effective date from insurance information in the document.

    Look for dates near these keywords:
    - "Effective date"
    - "Eligible" or "Eligibility"
    - "Issue date"
    - "Printed"
    - "Enrollment date"

    DO NOT extract dates from these sections:
    - "Encounter date"
    - "Visit date"
    - "Collection date"
    - "Auth effective"
    - "Referral effective"
    - "Referral is valid from"

    Format as YYYY-MM-DD.
  `),
    expirationDate: simpleDateSchema.nullable(),
    // Insurance-specific fields (nullable for self-pay)
    lineOfBusiness: z.nativeEnum(LineOfBusiness).nullable(),
    phoneNumber: phoneNumberSchema.nullable(),
    faxPhoneNumber: phoneNumberSchema.nullable(),
    groupNumber: z.string().nullable(),
    subscriberId: z
      .string()
      .nullable()
      .describe(
        "The subscriber/member ID for the insurance policy. " +
          'For VA (Veterans Affairs) referrals, use the veteran\'s ICN (Integrated Control Number) as the subscriberId instead of any other identifier on the document — it is always labeled "ICN".'
      ),
    beneficiary: z.string().nullable(),
    relationshipToSubscriber: z.nativeEnum(RelationshipToSubscriber).nullable(),
    payer: z
      .string()
      .nullable()
      .describe(
        "the entity that offers the insurance, such as Humana, UnitedHealthCare or Medicare. " +
          'For VA (Veterans Affairs) referrals, use "Optum VA Community Care Network" as the payer, even if the document lists a different or generic VA-related insurance name.'
      ),
    customerPayer: z
      .string()
      .nullable()
      .refine((val) => !val || payers.some((p) => p.primaryPayerId === val || p.shortId === val), {
        message: "customerPayer must be a valid payer primaryPayerId or shortId",
      })
      .describe(
        `Return the primaryPayerId matching the insurance carrier named in the document. If the carrier cannot be matched to any payer in this list, return null — do not use "Not Listed" or any placeholder value.
        For VA (Veterans Affairs) referrals, use "VACCN" (Veterans Affairs Community Care Network, administered by Optum).
        Use the closest match by name. "TRICARE FOR LIFE" matches TDFIC, "Medicare" alone (without a state qualifier) can match the Medicare entry for this state, and abbreviated or informal payer names match their full-form counterparts in the list.
        Available payers: ${payers.map((p) => `${p.primaryPayerId ?? p.shortId} (${p.name}${p.otherName && p.otherName.length > 0 ? ` / ${p.otherName.join(" / ")}` : ""})`).join(", ")}`
      ),
    plan: z
      .string()
      .nullable()
      .describe(
        "the specific offering by the insurance company that details benefits, such as Choice Plus or Open Access." +
          "Normally found on the insurance card or listed after the company name."
      ),
    subscriberGivenName: z
      .string()
      .nullable()
      .describe(
        `
          The full name might be written in formats such as:
            1. First Middle Last (e.g., John Michael Smith)
            2. Last, First Middle (e.g., Smith, John Michael)
            3. Last, First M. (e.g., Smith, John M.)

            Only return the given or first name

            Examples:
            Input: "John Michael Smith"
            Output: "John"

            Input: "Smith, John M."
            Output: "John"

            Input: "Sarah Elizabeth Johnson"
            Output: "Sarah"

            Input: "Robert Brown"
            Output: "Robert"

            Input: "Williams, James A."
            Output: "James"
      `
      ),
    subscriberFamilyName: z
      .string()
      .nullable()
      .describe(
        `
        The full name might be written in formats such as:
            1. First Middle Last (e.g., John Michael Smith)
            2. Last, First Middle (e.g., Smith, John Michael)
            3. Last, First M. (e.g., Smith, John M.)

            Only return the family or last name

            Examples:
            Input: "John Michael Smith"
            Output: "Smith"

            Input: "Smith, John M."
            Output: "Smith"

            Input: "Sarah Elizabeth Johnson"
            Output: "Johnson"

            Input: "Robert Brown"
            Output: "Brown"

            Input: "Williams, James A."
            Output: "Williams"
      `
      ),
    subscriberSex: z
      .nativeEnum(PersonGender)
      .nullable()
      .describe(
        "The sex of the insurance subscriber / policy holder. Required by the EHR layer to file insurance coverage."
      ),
  })
  .describe("A coverage is an insurance policy that covers a patient's medical expenses")
  .transform((data) => {
    if (data.lineOfBusiness === LineOfBusiness.MEDICARE && typeof data.subscriberId === "string") {
      return { ...data, subscriberId: normalizeMedicareSubscriberId(data.subscriberId) };
    }

    return data;
  });

export type Coverage = z.infer<typeof coverageSchema> & {
  patientId: string | null;
  ehrId: string | null;
} & DatabaseDefaults;

export function getCoverageDisplayName({
  coverage,
  includesPlan = true,
  includeSubscriberName = true,
}: {
  coverage: Coverage | null | undefined;
  includesPlan?: boolean;
  includeSubscriberName?: boolean;
}): string {
  if (!coverage) {
    return "Unknown Coverage";
  }

  if (coverage.coverageType === CoverageType.SELF_PAY) {
    return "Self Pay";
  }

  const payer = coverage.payer || "Unknown Insurance";
  const plan = includesPlan && coverage.plan ? ` - ${coverage.plan}` : "";
  const subscriberName =
    includeSubscriberName && coverage.subscriberGivenName && coverage.subscriberFamilyName
      ? ` - ${[coverage.subscriberGivenName, coverage.subscriberFamilyName].filter(Boolean).join(" ")}`
      : "";

  return `${payer}${plan}${subscriberName}`.trim();
}

// Updated shape with coverage type
export const coverageShape: Coverage = {
  id: "",
  patientId: null,
  ehrId: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  groupId: "",
  coverageType: CoverageType.INSURANCE, // Default to insurance
  // Common fields
  order: null,
  effectiveDate: null,
  expirationDate: null,
  // Insurance-specific fields
  lineOfBusiness: null,
  phoneNumber: null,
  faxPhoneNumber: null,
  groupNumber: null,
  subscriberId: null,
  beneficiary: null,
  relationshipToSubscriber: null,
  plan: null,
  subscriberGivenName: null,
  subscriberFamilyName: null,
  subscriberSex: null,
  payer: null,
  customerPayer: null,
};

export function isPatientSubscriber(coverage: Coverage | null | undefined, patient: Patient): boolean {
  if (!coverage) {
    return false;
  }

  return personNamesMatch(
    { givenName: coverage.subscriberGivenName, familyName: coverage.subscriberFamilyName } as Person,
    patient
  );
}

export type CoverageErrors = ObjectErrors<Coverage>;

/**
 * Validates a Coverage object using the generic validateObject helper.
 * If fieldsToValidate is provided, only those fields are validated.
 */
export function validateCoverage(coverage: Coverage, fieldsToValidate?: (keyof Coverage)[]) {
  return validateObject<Coverage>(coverageSchema, coverage, fieldsToValidate);
}

const MBI_EXCLUDED_CHAR_SUBSTITUTIONS: Record<string, string> = {
  B: "8",
  I: "1",
  L: "1",
  O: "0",
  S: "5",
  Z: "2",
};

// MBI: 1–9 at position 1; digits at 4/7/10/11; letters at 2/5/8/9; either at 3/6; letters exclude B/I/L/O/S/Z.
const MBI_SHAPE_REGEX =
  /^[1-9][AC-HJ-KM-NP-RT-Y][0-9AC-HJ-KM-NP-RT-Y][0-9][AC-HJ-KM-NP-RT-Y][0-9AC-HJ-KM-NP-RT-Y][0-9][AC-HJ-KM-NP-RT-Y]{2}[0-9]{2}$/;

export function normalizeMedicareSubscriberId(subscriberId: string): string {
  const normalizedId = subscriberId
    .replace(/[-\s]/g, "")
    .toUpperCase()
    .split("")
    .map((char) => MBI_EXCLUDED_CHAR_SUBSTITUTIONS[char] ?? char)
    .join("");

  return MBI_SHAPE_REGEX.test(normalizedId) ? normalizedId : subscriberId;
}
